/**
 * Phase 60 Plan 60-03 — Phone-number E.164 field on PreferencesPage (SMS-04 component rename).
 *
 * Mirrors the Slack handle Card from Plan 41-09 (PreferencesPage Surface 7):
 *   - Card + Field + TextInput + optional Clear Btn
 *   - Save-on-blur (no separate Save/Verify button — backend Zod handles validation)
 *   - useToast on success / error / clear
 *
 * Endpoint contract: PATCH /api/auth/preferences with body { phoneNumber: string | null }.
 * Backend Zod regex `/^\+[1-9]\d{1,14}$/` re-validates (D-CHANNEL-04 / NOTIF46-04). The
 * frontend regex is defense-in-depth + a friendlier inline error before round-tripping.
 *
 * After a successful save the component calls `refreshUser()` so `authContext.user`
 * picks up the new value from GET /api/auth/me — the field flows through the
 * already-existing `User.phoneNumber` (renamed in Phase 60 Plan 60-01).
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Field } from '../ui/Field';
import { TextInput } from '../ui/TextInput';
import { Btn } from '../ui/Btn';
import { useToast } from '../ui/Toast';
import { updateUserPreferences } from '../../services/api/userPreferences';
import { useAuth } from '../../services/authContext';

const E164_REGEX = /^\+[1-9]\d{1,14}$/;

interface Props {
  initialValue: string | null;
  onSaved?: (newValue: string | null) => void;
}

export function PhoneNumberField({ initialValue, onSaved }: Props) {
  const [phoneNumber, setPhoneNumber] = useState(initialValue ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const toast = useToast();
  const { refreshUser } = useAuth();

  const saveNumber = useMutation({
    mutationFn: (n: string | null) => updateUserPreferences({ phoneNumber: n }),
    onSuccess: (_, n) => {
      toast(n === null ? 'Phone number cleared' : 'Phone number saved', 'success');
      onSaved?.(n);
      // Re-fetch /api/auth/me so authContext.user.phoneNumber updates.
      void refreshUser();
    },
    onError: () => {
      toast('Failed to save phone number. Refresh the page and try again.', 'error');
    },
  });

  function handleBlur() {
    const trimmed = phoneNumber.trim() || null;
    if (trimmed && !E164_REGEX.test(trimmed)) {
      setValidationError('Phone number must be E.164 format (e.g. +420123456789)');
      return;
    }
    setValidationError(null);
    if (trimmed === (initialValue ?? null)) return; // no-op
    saveNumber.mutate(trimmed);
  }

  function handleClear() {
    setPhoneNumber('');
    setValidationError(null);
    saveNumber.mutate(null);
  }

  return (
    <Card title="Phone">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Field label="Phone number" error={validationError ?? undefined}>
            <TextInput
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              onBlur={handleBlur}
              placeholder="+420123456789"
            />
          </Field>
        </div>
        {phoneNumber && (
          <Btn variant="ghost" size="md" onClick={handleClear}>
            Clear
          </Btn>
        )}
      </div>
      <p className="text-[11px] text-ms-text-muted mt-3">
        International E.164 format with leading + and country code.
      </p>
    </Card>
  );
}
